import { ed, svgText, niceMax } from "./tokens";
import { useTooltip } from "./useTooltip";

export interface ReferenceRow {
  label: string;
  /** The person (or group) the figure is about. */
  value: number | null;
  /** Comparison points, drawn in gray. */
  references: Array<{ label: string; value: number | null }>;
  format?: (v: number) => string;
  /** When smaller is better (e.g. hours to merge), say so in the label. */
  lowerIsBetter?: boolean;
}

interface ReferenceDotsProps {
  rows: ReferenceRow[];
  subjectLabel: string;
  ariaLabel: string;
}

const W = 640;
const ROW = 46;
const M = { t: 12, r: 120, b: 6, l: 170 };

/**
 * A dumbbell per measure, each on its own scale: the subject's dot in the
 * emphasis color against gray reference dots (team median, last period).
 */
export function ReferenceDots({ rows, subjectLabel, ariaLabel }: ReferenceDotsProps) {
  const { bind, node } = useTooltip();
  const H = M.t + rows.length * ROW + M.b;
  const iw = W - M.l - M.r;

  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W }} role="img" aria-label={ariaLabel}>
        {rows.map((r, i) => {
          const fmt = r.format ?? ((v: number) => String(Math.round(v)));
          const vals = [r.value, ...r.references.map((x) => x.value)].filter(
            (v): v is number => v !== null && Number.isFinite(v),
          );
          const max = niceMax(Math.max(1, ...vals), 2);
          const x = (v: number) => M.l + (iw * v) / max;
          const cy = M.t + i * ROW + 20;
          const lo = vals.length ? Math.min(...vals) : 0;
          const hi = vals.length ? Math.max(...vals) : 0;
          return (
            <g key={r.label}>
              <line x1={M.l} x2={W - M.r} y1={cy} y2={cy} stroke={ed.grid} />
              <text x={M.l - 10} y={cy + 4} textAnchor="end" style={svgText.strong}>
                {r.label}
              </text>
              {r.lowerIsBetter && (
                <text x={M.l - 10} y={cy + 17} textAnchor="end" style={svgText.axis}>
                  lower is faster
                </text>
              )}
              {[0, max].map((t) => (
                <text key={t} x={x(t)} y={cy + 19} textAnchor="middle" style={svgText.axis}>
                  {fmt(t)}
                </text>
              ))}
              <line x1={x(lo)} x2={x(hi)} y1={cy} y2={cy} stroke={ed.markStrong} strokeWidth={2} />
              {r.references.map((ref) =>
                ref.value === null ? null : (
                  <circle
                    key={ref.label}
                    cx={x(ref.value)}
                    cy={cy}
                    r={5.5}
                    fill={ed.paper}
                    stroke={ed.markStrong}
                    strokeWidth={2}
                    {...bind(
                      <>
                        <b>{ref.label}</b>
                        {r.label}: <span className="num">{fmt(ref.value)}</span>
                      </>,
                    )}
                  />
                ),
              )}
              {r.value !== null && (
                <>
                  <circle
                    cx={x(r.value)}
                    cy={cy}
                    r={7}
                    fill={ed.emph}
                    stroke={ed.paper}
                    strokeWidth={2}
                    {...bind(
                      <>
                        <b>{subjectLabel}</b>
                        {r.label}: <span className="num">{fmt(r.value)}</span>
                      </>,
                    )}
                  />
                  <text
                    x={x(r.value)}
                    y={cy - 11}
                    textAnchor="middle"
                    style={svgText.numStrong}
                  >
                    {fmt(r.value)}
                  </text>
                </>
              )}
              {r.references.map((ref, k) =>
                ref.value === null || i > 0 ? null : (
                  <text
                    key={`l-${ref.label}`}
                    x={W - M.r + 8}
                    y={cy - 6 + k * 12}
                    style={svgText.axis}
                  >
                    ○ {ref.label}
                  </text>
                ),
              )}
            </g>
          );
        })}
      </svg>
      {node}
    </>
  );
}
